import { Dumbbell, Home, ArrowLeft, Zap, Swords, Heart, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const LandingPage = ({ onSelectWorkoutType }) => {
  const navigate = useNavigate();
  const [skillLevel, setSkillLevel] = useState('beginner');
  const [showIntensitySelector, setShowIntensitySelector] = useState(false);

  useEffect(() => {
    // Load current skill level
    const loadSkillLevel = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSkillLevel(response.data.skill_level || 'beginner');
      } catch (error) {
        console.error('Error loading skill level:', error);
      }
    };
    loadSkillLevel();
  }, []);

  const handleUpdateSkillLevel = async (newLevel) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/user/profile`,
        { skill_level: newLevel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSkillLevel(newLevel);
      setShowIntensitySelector(false);
      toast.success(`Intensity set to ${newLevel.toUpperCase()}`);
    } catch (error) {
      console.error('Error updating skill level:', error);
      toast.error('Failed to update intensity');
    }
  };

  const handleSelectWorkoutType = (type) => {
    // Always persist the selected training environment.
    onSelectWorkoutType(type);

    // Mindful Sanctuary keeps its dedicated browsing experience,
    // while Quick Workout can use the same saved environment to
    // launch a scheduled Yoga/Pilates workout directly.
    if (type === 'yoga_pilates') {
      navigate('/active-recovery');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 flex items-center justify-center p-4">
      <div className="max-w-7xl w-full animate-fade-in">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/hub')}
            className="glass border-stone-700 text-stone-300 hover:bg-stone-800 hover:border-stone-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Hub
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-blood-900 blur-3xl opacity-30 animate-pulse"></div>
              <Dumbbell className="w-20 h-20 text-blood-600 relative" />
            </div>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-wider" data-testid="landing-title">
            ANVIL & ASH
          </h1>
          <p className="text-lg sm:text-xl text-stone-400 max-w-2xl mx-auto uppercase tracking-wide">
            Strength. Discipline. Training that fits your life.
          </p>
          
          {/* Intensity Selector */}
          <div className="mt-8 flex justify-center">
            <Button
              variant="outline"
              onClick={() => setShowIntensitySelector(!showIntensitySelector)}
              className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold px-6"
            >
              <Zap className="w-4 h-4 mr-2" />
              INTENSITY: {skillLevel.toUpperCase()}
            </Button>
          </div>

          {/* Intensity Dropdown */}
          {showIntensitySelector && (
            <div className="mt-4 flex justify-center gap-3">
              <Button
                onClick={() => handleUpdateSkillLevel('beginner')}
                className={`${
                  skillLevel === 'beginner'
                    ? 'bg-blood-700 text-white'
                    : 'glass border-stone-700 text-stone-400 hover:bg-stone-800'
                } font-semibold`}
              >
                BEGINNER
              </Button>
              <Button
                onClick={() => handleUpdateSkillLevel('intermediate')}
                className={`${
                  skillLevel === 'intermediate'
                    ? 'bg-blood-700 text-white'
                    : 'glass border-stone-700 text-stone-400 hover:bg-stone-800'
                } font-semibold`}
              >
                INTERMEDIATE
              </Button>
              <Button
                onClick={() => handleUpdateSkillLevel('advanced')}
                className={`${
                  skillLevel === 'advanced'
                    ? 'bg-blood-700 text-white'
                    : 'glass border-stone-700 text-stone-400 hover:bg-stone-800'
                } font-semibold`}
              >
                ADVANCED
              </Button>
            </div>
          )}
        </div>

        {/* Selection Cards */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto mb-8">
          {/* Home Workout Card */}
          <Card 
            className="glass border-2 border-transparent hover:border-blood-800 p-8 cursor-pointer card-hover group transition-all duration-300 hover:shadow-2xl hover:shadow-blood-900/50"
            onClick={() => handleSelectWorkoutType('home')}
            data-testid="home-workout-card"
          >
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-lg bg-gradient-to-br from-blood-900 to-blood-950 border-2 border-blood-800 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blood-900/50">
                <Home className="w-12 h-12 text-blood-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-3 tracking-wider">HOME ARENA</h2>
                <p className="text-stone-400 text-base leading-relaxed uppercase tracking-wide">
                  Bodyweight, dumbbells, resistance bands. No gym required.
                </p>
              </div>
              <div className="pt-4 space-y-2">
                <div className="inline-flex items-center text-sm text-stone-400 font-medium uppercase tracking-wide">
                  <span className="mr-2 text-blood-600">⚔</span> Bodyweight
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-blood-600">⚔</span> Dumbbells
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-blood-600">⚔</span> Bands
                </div>
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-blood-900 to-blood-950 hover:from-blood-800 hover:to-blood-900 text-white font-semibold py-6 rounded-lg btn-primary border-2 border-blood-800 hover:border-blood-700 shadow-lg shadow-blood-900/50 uppercase tracking-widest"
                data-testid="select-home-btn"
              >
                Enter Arena
              </Button>
            </div>
          </Card>

          {/* Gym Workout Card */}
          <Card 
            className="glass border-2 border-transparent hover:border-stone-600 p-8 cursor-pointer card-hover group transition-all duration-300 hover:shadow-2xl hover:shadow-stone-900/50"
            onClick={() => handleSelectWorkoutType('gym')}
            data-testid="gym-workout-card"
          >
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-lg bg-gradient-to-br from-stone-800 to-stone-950 border-2 border-stone-700 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-stone-900/50">
                <Dumbbell className="w-12 h-12 text-stone-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-3 tracking-wider">GYM STRONGHOLD</h2>
                <p className="text-stone-400 text-base leading-relaxed uppercase tracking-wide">
                  Full arsenal. Barbells, machines, cables. Maximum power.
                </p>
              </div>
              <div className="pt-4 space-y-2">
                <div className="inline-flex items-center text-sm text-stone-400 font-medium uppercase tracking-wide">
                  <span className="mr-2 text-stone-500">⚔</span> Barbells
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-stone-500">⚔</span> Machines
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-stone-500">⚔</span> Cables
                </div>
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-stone-800 to-stone-950 hover:from-stone-700 hover:to-stone-900 text-white font-semibold py-6 rounded-lg btn-primary border-2 border-stone-700 hover:border-stone-600 shadow-lg shadow-stone-900/50 uppercase tracking-widest"
                data-testid="select-gym-btn"
              >
                Enter Stronghold
              </Button>
            </div>
          </Card>

          {/* Calisthenics Card */}
          <Card
            className="glass border-2 border-transparent hover:border-amber-600 p-8 cursor-pointer card-hover group transition-all duration-300 hover:shadow-2xl hover:shadow-amber-900/40"
            onClick={() => handleSelectWorkoutType('calisthenics')}
            data-testid="calisthenics-workout-card"
          >
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-lg bg-gradient-to-br from-amber-900 to-stone-950 border-2 border-amber-700 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-900/40">
                <Zap className="w-12 h-12 text-amber-400" />
              </div>

              <div>
                <h2 className="text-3xl font-bold text-white mb-3 tracking-wider">
                  CALISTHENICS
                </h2>

                <p className="text-stone-400 text-base leading-relaxed uppercase tracking-wide">
                  Bodyweight strength. Minimal equipment. Train anywhere.
                </p>
              </div>

              <div className="pt-4 space-y-2 text-sm text-stone-400 font-medium uppercase tracking-wide">
                <div>Bodyweight</div>
                <div>Pull-up Bar</div>
                <div>Rings / Dip Bars</div>
              </div>

              <Button
                className="w-full bg-gradient-to-r from-amber-800 to-stone-950 hover:from-amber-700 hover:to-stone-900 text-white font-semibold py-6 rounded-lg btn-primary border-2 border-amber-700 hover:border-amber-600 uppercase tracking-widest"
                data-testid="select-calisthenics-btn"
              >
                Start Calisthenics
              </Button>
            </div>
          </Card>

          {/* Yoga/Pilates Focus Card */}
          <Card 
            className="glass border-2 border-transparent hover:border-purple-600 p-8 cursor-pointer card-hover group transition-all duration-300 hover:shadow-2xl hover:shadow-purple-900/50"
            onClick={() => handleSelectWorkoutType('yoga_pilates')}
            data-testid="yoga-pilates-workout-card"
          >
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-lg bg-gradient-to-br from-purple-900 to-purple-950 border-2 border-purple-700 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-purple-900/50">
                <Heart className="w-12 h-12 text-purple-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-3 tracking-wider">MINDFUL SANCTUARY</h2>
                <p className="text-stone-400 text-base leading-relaxed uppercase tracking-wide">
                  Yoga, Pilates, Stretching. Balance mind & body.
                </p>
              </div>
              <div className="pt-4 space-y-2">
                <div className="inline-flex items-center text-sm text-stone-400 font-medium uppercase tracking-wide">
                  <span className="mr-2 text-purple-500">☯</span> Yoga
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-purple-500">☯</span> Pilates
                </div>
                <div className="inline-flex items-center text-sm text-stone-400 font-medium ml-4 uppercase tracking-wide">
                  <span className="mr-2 text-purple-500">☯</span> Cardio
                </div>
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-purple-800 to-purple-950 hover:from-purple-700 hover:to-purple-900 text-white font-semibold py-6 rounded-lg btn-primary border-2 border-purple-700 hover:border-purple-600 shadow-lg shadow-purple-900/50 uppercase tracking-widest"
                data-testid="select-yoga-btn"
              >
                Enter Sanctuary
              </Button>
            </div>
          </Card>
        </div>

        {/* Challenge a Friend Tile */}
        <div className="max-w-2xl mx-auto mb-8 grid sm:grid-cols-2 gap-6">
          <Card 
            className="glass border-2 border-transparent hover:border-emerald-600 p-6 cursor-pointer card-hover group transition-all duration-300 hover:shadow-xl hover:shadow-emerald-800/40"
            onClick={() => navigate('/log-activity')}
            data-testid="log-activity-tile"
          >
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-emerald-800 to-emerald-950 border-2 border-emerald-600 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-emerald-800/40">
                <MapPin className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1 tracking-wider">LOG ACTIVITY</h3>
                <p className="text-stone-400 text-sm leading-relaxed uppercase tracking-wide">
                  Trails, runs, sports. Log anything done off the app.
                </p>
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-emerald-700 to-emerald-900 hover:from-emerald-600 hover:to-emerald-800 text-white font-semibold py-4 rounded-lg border-2 border-emerald-600 hover:border-emerald-500 shadow-lg shadow-emerald-800/40 uppercase tracking-widest"
              >
                Field Log
              </Button>

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/log-activity?activity=Running');
                }}
                className="w-full bg-gradient-to-r from-sky-800 to-sky-950 hover:from-sky-700 hover:to-sky-900 text-white font-semibold py-4 rounded-lg border-2 border-sky-700 hover:border-sky-600 shadow-lg shadow-sky-900/40 uppercase tracking-widest"
                data-testid="run-jog-entry-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Run / Jog
              </Button>
            </div>
          </Card>

          <Card 
            className="glass border-2 border-transparent hover:border-blood-600 p-6 cursor-pointer card-hover group transition-all duration-300 hover:shadow-xl hover:shadow-blood-700/50"
            onClick={() => navigate('/friends')}
          >
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-blood-800 to-blood-950 border-2 border-blood-600 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blood-800/50">
                <Swords className="w-8 h-8 text-blood-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1 tracking-wider">CHALLENGE A FRIEND</h3>
                <p className="text-stone-400 text-sm leading-relaxed uppercase tracking-wide">
                  Pick a friend. Train. See who comes out on top.
                </p>
              </div>
              <Button 
                className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold py-4 rounded-lg border-2 border-blood-600 hover:border-blood-500 shadow-lg shadow-blood-800/50 uppercase tracking-widest"
              >
                Start a Challenge
              </Button>
            </div>
          </Card>
        </div>

        {/* Features */}
        <div className="mt-20 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="glass p-6 rounded-xl">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-white font-semibold mb-2">Personalized Plans</h3>
              <p className="text-blue-200 text-sm">Personalized workout plans built around your goals, equipment, and experience level</p>
            </div>
            <div className="glass p-6 rounded-xl">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="text-white font-semibold mb-2">Track Progress</h3>
              <p className="text-blue-200 text-sm">Monitor your weights, reps, and improvement over time</p>
            </div>
            <div className="glass p-6 rounded-xl">
              <div className="text-3xl mb-3">💪</div>
              <h3 className="text-white font-semibold mb-2">Stay Consistent</h3>
              <p className="text-blue-200 text-sm">Structured routines that rotate through all muscle groups</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
